import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  MapPin,
  FlaskConical,
  MessageSquare,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAutoUpdate } from '@/hooks/useAutoUpdate';

const NAV = [
  { to: '/', key: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/users', key: 'nav.users', icon: Users },
  { to: '/points', key: 'nav.points', icon: MapPin },
  { to: '/research', key: 'nav.research', icon: FlaskConical },
  { to: '/feedback', key: 'nav.feedback', icon: MessageSquare },
] as const;

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, key, icon: Icon, ...rest }) => (
        <NavLink
          key={to}
          to={to}
          end={'end' in rest}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
              isActive
                ? 'bg-stone-700 text-white'
                : 'text-stone-300 hover:bg-stone-800 hover:text-white'
            }`
          }
        >
          <Icon size={18} />
          {t(key)}
        </NavLink>
      ))}
    </nav>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  useAutoUpdate();

  const version = import.meta.env.VITE_APP_VERSION ?? 'dev';

  return (
    <div className="min-h-screen md:flex">
      {/* Barra superior (móvil) */}
      <header className="flex items-center justify-between bg-stone-900 px-4 py-3 text-white md:hidden">
        <span className="font-semibold">Bestigium · Backoffice</span>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
          className="rounded p-1 hover:bg-stone-800"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {menuOpen && (
        <div className="bg-stone-900 px-4 pb-4 text-white md:hidden">
          <NavItems onNavigate={() => setMenuOpen(false)} />
          <button
            onClick={logout}
            className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-stone-300 hover:bg-stone-800 hover:text-white"
          >
            <LogOut size={18} />
            {t('nav.logout')}
          </button>
          <p className="mt-3 px-3 text-xs text-stone-500">
            {user?.email} · v{version}
          </p>
        </div>
      )}

      {/* Sidebar (escritorio) */}
      <aside className="hidden w-60 shrink-0 flex-col bg-stone-900 p-4 text-white md:flex md:min-h-screen">
        <div className="mb-6 px-3">
          <p className="text-lg font-semibold">Bestigium</p>
          <p className="text-xs uppercase tracking-widest text-amber-500">Backoffice</p>
        </div>
        <NavItems />
        <div className="mt-auto">
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-stone-300 hover:bg-stone-800 hover:text-white"
          >
            <LogOut size={18} />
            {t('nav.logout')}
          </button>
          <p className="mt-3 truncate px-3 text-xs text-stone-500" title={user?.email ?? ''}>
            {user?.email}
          </p>
          <p className="px-3 text-xs text-stone-600">v{version}</p>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
